import React, { FC, useCallback, useMemo, useRef } from 'react'
import { Story, TStory } from '../index'
import { FeedForm } from '../../../modules/Feed/components'
import { TStoryActionOption } from '../../../shared/types'
import { scrollToTop } from '../../../shared/utils'
import { useStory } from '../../../modules/Feed/serviceHooks'
import { useConfirmation } from '../../Project/Confirmation'
import { StoryActionsDropdown } from '../blocks/StoryActionsDropdown'
import { ModalAPI } from '../common/StoryShareModel'
import { useRemoveFeed, useUpdateFeed } from '../../../modules/Feed/graphql/mutations'
import { useShareStoryToGlobalhub } from '../common/ShareToGlobalhub/useShareStoryToGlobalHub'
import { useAbility } from '@casl/react'
import { AbilityContext } from '../../../shared/abilities'
import { subject } from '@casl/ability'
import { useReactiveVar } from '@apollo/client'
import { activeProjectVar } from '../../../modulesCommon/store'
import { useStoryPinning } from 'modules/Feed/serviceHooks/useStoryPinning'
import { ENABLED_FEATURES } from '../../../shared/utils/featureFlags'
import { Tag } from 'shared/__gql-types'

type TLocalStoryProps = {
  story: TStory
  dataTestPrefix?: string
  tagsList: {
    id: string
    label?: string | null,
    order_number?: number | null
  }[]
  handleModal?(api: ModalAPI): () => void
}

export const LocalStory: FC<TLocalStoryProps> = (props) => {
  const storyRef = useRef<HTMLDivElement>(null)
  const {
    story: s,
    dataTestPrefix,
    tagsList,
    handleModal
  } = props
  const ability = useAbility(AbilityContext)
  const p = useReactiveVar(activeProjectVar)
  const story = useMemo(() => ({ ...s }), [s])
  const activeProject = { ...p }
  const [isEditMode, setEditMode] = React.useState(false)
  const { mutate: updateFeed } = useUpdateFeed()
  const { mutate: removeFeed } = useRemoveFeed()

  const { removeFeedHandler } = useStory({ story, removeFeed, storyRef })
  const { toggleStoryPinState } = useStoryPinning(story, storyRef)
  const [confirmationPopup, shareWithGH] = useShareStoryToGlobalhub(story, handleModal)
  const [onConfirmDelete, deleteModal] = useConfirmation(() => removeFeedHandler(), {
    header: 'Delete Story',
    actionText: 'Delete'
  })
  const updateFeedHandler = useCallback(
    (args) =>
      updateFeed({
        variables: {
          input: {
            id: +story.id,
            campaign_id: `${story.campaign_id}`,
            ...args,
            tags: args.tags?.map(({ __typename: any, ...rest }: Tag) => rest) ?? []
          }
        }
      }).then(({ data: { updateFeed } }) => {
        if (!updateFeed.id) {
          // toggleError(true)
        } else {
          scrollToTop()
        }
      }),
    [story, updateFeed]
  )

  const isGlobalProjectExist = !!activeProject.parent_id
  const isStoryPublished = story.global_status === 'PUBLISHED'
  const canShareWithGlobalhub = ability.can('globalize', subject('Story', story))
  const canEdit = ability.can('edit', subject('Story', story))
  const canDelete = ability.can('delete', subject('Story', story))

  const storyActions = useMemo(() => [
    {
      key: 'pin',
      title: story.pinned ? 'Unpin Story' : 'Pin Story',
      action: () => toggleStoryPinState()
    }, {
      key: 'edit',
      title: 'Edit Story',
      action: () => setEditMode(true),
      hidden: !canEdit
    }, {
      key: 'delete',
      title: 'Delete Story',
      action: () => {
        onConfirmDelete({ data: null, message: 'Are you sure you want to delete this story?' })
      },
      hidden: !canDelete
    }, {
      key: 'share',
      title: 'Share with Global Hub',
      action: () => {
        shareWithGH()
      },
      hidden: ENABLED_FEATURES.GLOBALHUB ? !(canShareWithGlobalhub && isGlobalProjectExist && !isStoryPublished) : true
    }
  ] as TStoryActionOption[], [canDelete, canEdit, canShareWithGlobalhub, isGlobalProjectExist, isStoryPublished, onConfirmDelete, shareWithGH, toggleStoryPinState, story.pinned])

  return (
    <Story.Container story={story} storyRef={storyRef}>
      <Story.Header
        actions={
          !isEditMode ? <StoryActionsDropdown
            actions={storyActions}
          /> : null
        }
      />

      {!isEditMode ? (
        <>
          <Story.Body>
            <Story.Article />
            <Story.Gallery />
            <Story.Comments commentsFilterFn={(c) => {

              return String(c.campaign_id) === story.campaign.id
            }} />
          </Story.Body>

          <Story.Section>
            <Story.Meta />
            <Story.Tags />
          </Story.Section>

        </>
      ) : (
        <FeedForm
          handleCloseForm={() => setEditMode(false)}
          feedHandler={updateFeedHandler}
          tagsList={tagsList}
          initialValue={story.body || ''}
          initialGallery={story.gallery || []}
          initialTitle={story.title}
          initialTagsValue={story.tags}
          dataTestPrefix={`${dataTestPrefix}-post_edit`}
          isUpdateFeed
        />
      )}
      {confirmationPopup}
      {deleteModal}
    </Story.Container>
  )
}
